/**
 * Portal Bendito Vendedor — página de entrada
 * ===========================================
 *
 * Fase 1: solo la experiencia de entrada.
 *
 *   #/           portada, los dos caminos
 *   #/solicitud  formulario en tres pasos
 *   #/enviada    confirmación
 *
 * LO QUE ESTE ARCHIVO NO HACE, A PROPÓSITO
 * ----------------------------------------
 * No verifica a nadie. El formulario de ingreso comprueba el FORMATO del
 * correo y del código, y luego dice que el ingreso todavía no está conectado.
 * No guarda sesión y no finge: un ingreso de mentira que "funciona" enseñaría
 * a los vendedores a confiar en algo que no existe.
 *
 * NO HAY CONTRASEÑAS en este modelo. La identificación es correo + código
 * BENDIT@###, un identificador que asigna Bendito Equilibrio al aprobar.
 *
 * Tampoco envía la solicitud. La guarda en localStorage para que quien la
 * escribió no la pierda si recarga, y nada más. Cuando haya backend, el punto
 * de conexión es `enviarSolicitud()`: es la única función que hay que cambiar.
 */

(() => {
  'use strict';

  const raiz = document.querySelector('[data-be-portal]');
  if (!raiz) return;

  const CLAVE_BORRADOR = 'be-portal-solicitud';

  /* Configuración de Firestore. La imprime el snippet desde los ajustes del
     tema; aquí NUNCA se escriben claves. Si falta alguna, `cfg` queda vacío y
     nada intentará conectarse: es el estado seguro. */
  const cfg = (() => {
    const caja = document.querySelector('[data-be-portal-config]');
    if (!caja) return {};
    try {
      const c = JSON.parse(caja.textContent);
      return c.apiKey && c.projectId ? c : {};
    } catch (_) {
      return {};
    }
  })();

  const hayFirebase = Boolean(cfg.apiKey && cfg.projectId);

  /* ---------------------------------------------------------------- utilidades */

  const $ = (sel, ctx = raiz) => ctx.querySelector(sel);
  const $$ = (sel, ctx = raiz) => [...ctx.querySelectorAll(sel)];

  /* ------------------------------------------------------------------- vistas */

  const vistas = $$('[data-vista]');

  /* Las rutas de la fase 2 se añaden aquí y en el snippet. El enrutado ya las
     admite: cualquier hash que no esté en la lista cae en la portada. */
  const RUTAS = {
    '#/': 'portada',
    '': 'portada',
    '#/solicitud': 'solicitud',
    '#/enviada': 'enviada',
    '#/portal': 'portal',
    '#/galeria': 'galeria',
  };

  /* Las categorías de la biblioteca. Salen de carpetas reales de recursos, no
     de una invención: ver el comentario de la vista en el snippet. */
  const CATEGORIAS = {
    estados: { nombre: 'Estados para WhatsApp', total: 14 },
    productos: { nombre: 'Productos sin fondo', total: 14 },
  };

  /* WhatsApp oficial de Bendito Equilibrio, el mismo del botón flotante de la
     tienda. Es de la marca, no de ningún vendedor: por eso sí va escrito. */
  const WA_BENDITO = '573203156115';

  /* ------------------------------------------------------ el viaje de color */

  /* Solo escribe `data-tono`. El resto —neón, manchas, ondas, botones, fichas,
     halos— lo mueve el CSS solo, porque todo cuelga de --acc y --fuerza. */
  function tono(valor) {
    if (raiz.dataset.tono === String(valor)) return;
    raiz.dataset.tono = valor;
    destellar();
  }

  const destello = $('[data-destello]');

  function destellar() {
    if (!destello) return;
    destello.classList.remove('is-vivo');
    // Forzar reflujo: sin esto la animación no se reinicia si ya se lanzó.
    void destello.offsetWidth;
    destello.classList.add('is-vivo');
  }

  function mostrar(nombre) {
    vistas.forEach((v) => {
      const activa = v.dataset.vista === nombre;
      v.hidden = !activa;
      v.classList.toggle('is-activa', activa);
      // Reinicia la animación de entrada al volver a una vista ya visitada.
      if (activa) {
        v.style.animation = 'none';
        void v.offsetWidth;
        v.style.animation = '';
      }
    });
    window.scrollTo({ top: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
  }

  function enrutar() {
    const hash = window.location.hash;

    /* La biblioteca tiene dos niveles: `#/galeria` son las categorías y
       `#/galeria/estados` es una categoría abierta. Se resuelve por prefijo
       porque el segundo trozo es variable. */
    const enGaleria = hash.startsWith('#/galeria');
    const categoria = enGaleria ? hash.split('/')[2] : null;
    const nombre = enGaleria ? 'galeria' : RUTAS[hash] || 'portada';

    /* La biblioteca es para vendedores: sin sesión, a la portada. */
    if (nombre === 'galeria' && !vendedorActivo) {
      window.location.hash = '#/';
      return;
    }

    /* El portal es privado: sin sesión no se entra aunque se pegue el hash.
       No es seguridad —los datos reales los protege Firestore— pero evita
       enseñar una pantalla vacía a quien no ha entrado. */
    if (nombre === 'portal' && !vendedorActivo) {
      window.location.hash = '#/';
      return;
    }
    // A la confirmación solo se llega enviando: si alguien pega #/enviada en
    // la barra sin haber enviado nada, se le manda a la portada.
    if (nombre === 'enviada' && !enviada) {
      window.location.hash = '#/';
      return;
    }
    mostrar(nombre);

    if (nombre === 'portal') pintarPortal();
    if (nombre === 'galeria') pintarGaleria(categoria);
    if (nombre === 'enviada') pintarConfirmacion();

    if (nombre === 'portada') tono('0');
    else if (nombre === 'solicitud') tono(String(etapaActual));
    else if (nombre === 'enviada') tono('final');
    else if (nombre === 'portal') tono('1');
    else if (nombre === 'galeria') tono('2');
  }

  /* --------------------------------------------- confirmación de solicitud
   *
   * Arma el aviso por WhatsApp. El mensaje lleva SOLO el nombre y la
   * referencia: ni documento, ni fecha de nacimiento, ni dirección, ni
   * correo. Un WhatsApp pasa por el teléfono de la persona, por copias de
   * seguridad y por quien mire la pantalla; ahí no tiene por qué viajar nada
   * más que un aviso de "ya la envié".
   */
  function pintarConfirmacion() {
    if (!solicitudEnviada) return;

    const saludo = $('[data-be-gracias-nombre]');
    if (saludo) saludo.textContent = solicitudEnviada.nombre ? `, ${solicitudEnviada.nombre}` : '';

    const ref = $('[data-be-referencia]');
    if (ref && solicitudEnviada.referencia) {
      ref.hidden = false;
      ref.textContent = `Referencia de tu solicitud: ${solicitudEnviada.referencia}`;
    }

    const boton = $('[data-be-avisar-wa]');
    if (!boton) return;

    let texto = 'Hola Bendito Equilibrio, acabo de hacer la solicitud para ser vendedor/a.';
    if (solicitudEnviada.nombre) texto += ` Mi nombre es ${solicitudEnviada.nombre}.`;
    if (solicitudEnviada.referencia) {
      texto += ` Mi código de solicitud es ${solicitudEnviada.referencia}.`;
    }

    /* wa.me abre la app en el móvil y WhatsApp Web en el escritorio, con el
       mensaje escrito y SIN enviarlo. No existe forma de enviarlo solo, y es
       lo correcto: el mensaje sale del teléfono de quien solicita. */
    boton.href = `https://wa.me/${WA_BENDITO}?text=${encodeURIComponent(texto)}`;
  }

  /* ------------------------------------------------ biblioteca de contenido
   *
   * Dos niveles dentro de la misma vista: las categorías, o una categoría
   * abierta. Las dos galerías están en el marcado —así las rutas de los
   * assets las resuelve Liquid— y aquí solo se decide cuál se enseña.
   */
  function pintarGaleria(categoria) {
    const listado = $('[data-be-categorias]');
    const abierta = $('[data-be-categoria-abierta]');
    if (!listado || !abierta) return;

    const cat = categoria && CATEGORIAS[categoria] ? categoria : null;

    listado.hidden = Boolean(cat);
    abierta.hidden = !cat;

    $$('[data-be-galeria]').forEach((g) => {
      g.hidden = g.dataset.beGaleria !== cat;
    });

    if (!cat) return;

    const info = CATEGORIAS[cat];
    const titulo = $('[data-be-categoria-titulo]');
    const conteo = $('[data-be-categoria-conteo]');
    if (titulo) titulo.textContent = info.nombre;
    if (conteo) {
      conteo.textContent =
        `${info.total} imágenes · toca “Descargar” para guardarla en tu dispositivo.`;
    }
  }

  /* ------------------------------------------------------- el portal privado
   *
   * Rellena la vista con la ficha del vendedor que entró. TODO sale de
   * `vendedorActivo`: ni un nombre, ni un código, ni una ruta escritos a mano.
   * Un solo portal, seis vendedores, seis contenidos distintos.
   */
  /* ==================================================================
     EL PEDIDO DEL VENDEDOR
     ------------------------------------------------------------------
     Una sola regla, en un solo sitio: el vendedor paga el 70% y gana el
     30%. Si un día cambia el porcentaje, se cambia AQUÍ y cambia todo:
     tarjetas, resumen, barra y lo que se guarde en Firestore.
     ================================================================== */

  const PORCENTAJE_VENDEDOR = 0.7;

  /** Pesos colombianos, sin decimales: aquí no existe el centavo. */
  const pesos = (n) => '$' + Math.round(n).toLocaleString('es-CO');

  /* Liquid entrega los precios en CENTAVOS. Se dividen una vez, al leer, y en
     el resto del archivo ya son pesos. */
  const productos = (() => {
    const caja = document.querySelector('[data-be-productos-portal]');
    if (!caja) return [];
    try {
      return JSON.parse(caja.textContent).map((p) => ({ ...p, precio: p.precio / 100 }));
    } catch (_) {
      return [];
    }
  })();

  /** handle → cantidad. Vive solo mientras dura la visita. */
  const pedido = new Map();

  const precioVendedor = (precioCliente) => precioCliente * PORCENTAJE_VENDEDOR;

  function cuentasDelPedido() {
    let unidades = 0;
    let totalCliente = 0;

    pedido.forEach((cantidad, id) => {
      const p = productos.find((x) => x.id === id);
      if (!p) return;
      unidades += cantidad;
      totalCliente += p.precio * cantidad;
    });

    /* Se calcula sobre el TOTAL, no sumando líneas redondeadas: redondear
       cada línea y luego sumar desvía el resultado unos pesos. */
    const totalBendito = totalCliente * PORCENTAJE_VENDEDOR;
    return {
      unidades,
      totalCliente,
      totalBendito,
      comision: totalCliente - totalBendito,
    };
  }

  function pintarProductos() {
    const lista = $('[data-be-productos-lista]');
    if (!lista) return;

    if (!productos.length) {
      lista.innerHTML =
        '<p class="be-por__vacio">No pudimos cargar el catálogo en este momento. ' +
        'Recarga la página o escríbenos por WhatsApp.</p>';
      return;
    }

    lista.innerHTML = '';

    for (const p of productos) {
      const cantidad = pedido.get(p.id) || 0;
      const tarjeta = document.createElement('article');
      tarjeta.className = 'be-por__producto' + (cantidad ? ' is-elegido' : '');
      tarjeta.dataset.beProducto = p.id;

      tarjeta.innerHTML = `
        <div class="be-por__producto-foto"><img src="${p.foto}" alt="" loading="lazy"></div>
        <h3 class="be-por__producto-nombre"></h3>
        <dl class="be-por__precios">
          <div><dt>Precio cliente</dt><dd>${pesos(p.precio)}</dd></div>
          <div class="be-por__precio--tuyo"><dt>Tu precio</dt><dd>${pesos(precioVendedor(p.precio))}</dd></div>
          <div class="be-por__precio--ganas"><dt>Tu comisión</dt><dd>${pesos(p.precio - precioVendedor(p.precio))}</dd></div>
        </dl>
        <div class="be-por__contador">
          <button type="button" class="be-por__paso-btn" data-menos aria-label="Quitar uno">−</button>
          <span class="be-por__cantidad" data-cantidad>${cantidad}</span>
          <button type="button" class="be-por__paso-btn" data-mas aria-label="Agregar uno">+</button>
        </div>
        <p class="be-por__producto-sub" data-sub hidden></p>
      `;

      // El nombre por textContent y no en la plantilla: viene del catálogo.
      tarjeta.querySelector('.be-por__producto-nombre').textContent = p.nombre;
      lista.append(tarjeta);
      pintarLineaProducto(tarjeta, p);
    }

    lista.addEventListener('click', (e) => {
      const boton = e.target.closest('[data-mas], [data-menos]');
      if (!boton) return;
      const tarjeta = boton.closest('[data-be-producto]');
      const p = productos.find((x) => x.id === tarjeta.dataset.beProducto);
      if (!p) return;

      const actual = pedido.get(p.id) || 0;
      const nueva = boton.hasAttribute('data-mas') ? actual + 1 : Math.max(0, actual - 1);

      if (nueva === 0) pedido.delete(p.id);
      else pedido.set(p.id, nueva);

      tarjeta.classList.toggle('is-elegido', nueva > 0);
      tarjeta.querySelector('[data-cantidad]').textContent = nueva;
      pintarLineaProducto(tarjeta, p);
      pintarPedido();
    });
  }

  function pintarLineaProducto(tarjeta, p) {
    const cantidad = pedido.get(p.id) || 0;
    const sub = tarjeta.querySelector('[data-sub]');
    sub.hidden = cantidad === 0;
    if (cantidad) {
      sub.textContent = `${cantidad} × ${pesos(precioVendedor(p.precio))} = ${pesos(precioVendedor(p.precio) * cantidad)}`;
    }
  }

  function pintarPedido() {
    const c = cuentasDelPedido();
    const vacio = $('[data-be-pedido-vacio]');
    const detalle = $('[data-be-pedido-detalle]');
    const barra = $('[data-be-barra-pedido]');
    if (!detalle) return;

    const hay = c.unidades > 0;
    vacio.hidden = hay;
    detalle.hidden = !hay;
    if (barra) barra.hidden = !hay;

    if (!hay) return;

    const lineas = $('[data-be-pedido-lineas]');
    lineas.innerHTML = '';
    pedido.forEach((cantidad, id) => {
      const p = productos.find((x) => x.id === id);
      if (!p) return;
      const li = document.createElement('li');
      const nombre = document.createElement('span');
      nombre.textContent = `${cantidad} × ${p.nombre}`;
      const valor = document.createElement('strong');
      valor.textContent = pesos(precioVendedor(p.precio) * cantidad);
      li.append(nombre, valor);
      lineas.append(li);
    });

    const poner = (sel, txt) => { const e = $(sel); if (e) e.textContent = txt; };
    poner('[data-be-total-unidades]', String(c.unidades));
    poner('[data-be-total-cliente]', pesos(c.totalCliente));
    poner('[data-be-total-bendito]', pesos(c.totalBendito));
    poner('[data-be-total-comision]', pesos(c.comision));
    poner('[data-be-barra-texto]', `${c.unidades} producto${c.unidades === 1 ? '' : 's'} · pagas`);
    poner('[data-be-barra-total]', pesos(c.totalBendito));
  }

  /**
   * PUNTO DE CONEXIÓN DE LOS PEDIDOS.
   *
   * Todavía NO guarda nada: la colección `pedidos` no existe y el reglamento
   * de Firestore niega todo lo que no esté nombrado, así que un intento de
   * escribir sería rechazado. Antes hace falta crear la colección y añadirle
   * su regla; está explicado en HANDOFF-PORTAL.md.
   *
   * El pedido ya se arma completo aquí, con el vendedor que lo hace, para que
   * conectarlo sea cambiar el cuerpo de esta función y nada más.
   */
  /**
   * Registro del pedido del vendedor.
   *
   * Hoy solo deja rastro en consola: la colección `pedidos` de Firestore aún
   * no existe. El pedido NO depende de esta función — sale por WhatsApp pase
   * lo que pase aquí. Cuando se cree la colección, este es el único sitio que
   * hay que tocar.
   */
  function guardarPedido(datos) {
    console.info('[Portal] Pedido enviado por WhatsApp. Sin registro: falta la colección `pedidos`.', datos);
    return Promise.resolve({ guardado: false });
  }

  function armarPedido() {
    const c = cuentasDelPedido();
    const v = vendedorActivo;
    return {
      vendedor_codigo: v.codigo,
      vendedor_nombre: v.nombre,
      vendedor_whatsapp: v.whatsapp,
      lineas: [...pedido.entries()].map(([id, cantidad]) => {
        const p = productos.find((x) => x.id === id);
        return {
          producto: id,
          nombre: p.nombre,
          cantidad,
          precio_cliente: p.precio,
          precio_vendedor: precioVendedor(p.precio),
        };
      }),
      unidades: c.unidades,
      total_cliente: Math.round(c.totalCliente),
      total_bendito: Math.round(c.totalBendito),
      comision: Math.round(c.comision),
      estado: 'pendiente',
    };
  }

  /* ================================================================
     GRÁFICOS
     ----------------------------------------------------------------
     SVG a mano, sin librería: son dos líneas y una cuadrícula, y
     cualquier paquete pesaría más que todo el portal.
     ================================================================ */

  function dibujarGrafico(caja, datos, formato) {
    const dias = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
    const hayDatos = datos.some((v) => v > 0);

    if (!hayDatos) {
      caja.innerHTML =
        `<p class="be-por__vacio be-por__vacio--grafico">${caja.dataset.beVacio}</p>`;
      return;
    }

    const W = 320;
    const H = 120;
    const max = Math.max(...datos) * 1.15 || 1;
    const punto = (i, v) => [
      (i / (dias - 1)) * W,
      H - (v / max) * H,
    ];

    const linea = datos.map((v, i) => punto(i, v).join(',')).join(' ');
    const area = `0,${H} ${linea} ${W},${H}`;

    caja.innerHTML = `
      <svg viewBox="0 0 ${W} ${H}" preserveAspectRatio="none" class="be-por__svg">
        <polygon points="${area}" class="be-por__area" />
        <polyline points="${linea}" class="be-por__linea" />
      </svg>
      <div class="be-por__eje">
        <span>1</span><span>${Math.round(dias / 2)}</span><span>${dias}</span>
      </div>
      <p class="be-por__grafico-pico">Máximo: ${formato(Math.max(...datos))}</p>
    `;
  }

  /**
   * PUNTO DE CONEXIÓN DE LAS VENTAS.
   *
   * Devolverá los pedidos del vendedor autenticado, y SOLO los suyos. Hoy
   * devuelve vacío porque no hay dónde consultarlos: sin pedidos guardados no
   * hay ventas que dibujar, y una línea inventada sería peor que un hueco.
   */
  function cargarPedidosDelMes() {
    return Promise.resolve([]);
  }

  async function pintarNegocio() {
    const dias = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
    const pedidosMes = await cargarPedidosDelMes();

    const ventasPorDia = new Array(dias).fill(0);
    const pedidosPorDia = new Array(dias).fill(0);
    let ventas = 0;
    let comision = 0;
    let pagado = 0;

    for (const p of pedidosMes) {
      const dia = (p.dia || 1) - 1;
      ventasPorDia[dia] += p.total_cliente || 0;
      pedidosPorDia[dia] += 1;
      ventas += p.total_cliente || 0;
      comision += p.comision || 0;
      pagado += p.total_bendito || 0;
    }

    const poner = (n, txt) => { const e = $(`[data-be-ind="${n}"]`); if (e) e.textContent = txt; };
    poner('ventas', pesos(ventas));
    poner('pedidos', String(pedidosMes.length));
    poner('comision', pesos(comision));
    poner('pagado', pesos(pagado));

    const gv = $('[data-be-grafico="ventas"]');
    const gp = $('[data-be-grafico="pedidos"]');
    if (gv) {
      gv.dataset.beVacio = 'Aún no tienes ventas registradas este mes.';
      dibujarGrafico(gv, ventasPorDia, pesos);
    }
    if (gp) {
      gp.dataset.beVacio = 'Aún no tienes pedidos registrados este mes.';
      dibujarGrafico(gp, pedidosPorDia, (n) => `${n} pedido${n === 1 ? '' : 's'}`);
    }
  }

  function pintarPortal() {
    const v = vendedorActivo;
    if (!v) return;

    const poner = (sel, texto) => $$(sel).forEach((e) => { e.textContent = texto; });

    poner('[data-be-nombre]', v.nombre_catalogo || v.nombre || '');
    poner('[data-be-codigo]', v.codigo || '');
    poner('[data-be-catalogo-ruta]', v.catalogo || '');
    poner('[data-be-whatsapp]', v.whatsapp ? '+' + v.whatsapp : '');

    /* El estado sale de Firestore. Si un día hay más estados, el punto verde
       solo se enciende con "activo". */
    const estado = (v.estado || '').toLowerCase();
    poner('[data-be-estado]', estado === 'activo' ? 'Vendedor activo' : `Estado: ${v.estado || '—'}`);
    $$('[data-be-estado]').forEach((e) => e.classList.toggle('is-activo', estado === 'activo'));

    // La URL completa, para compartir y para enseñarla tal como se verá.
    const urlCatalogo = v.catalogo ? new URL(v.catalogo, window.location.origin).href : '';
    poner('[data-be-catalogo-url]', urlCatalogo);
    $$('[data-be-catalogo-enlace]').forEach((a) => { a.href = v.catalogo || '#'; });

    /* Su WhatsApp: se abre su propio chat. El texto lo escribirá el cliente,
       así que no se prellena nada. */
    $$('[data-be-whatsapp-enlace]').forEach((a) => {
      a.href = v.whatsapp ? `https://wa.me/${v.whatsapp}` : '#';
    });

    /* El de Bendito: el mensaje se identifica con su código para que quien
       atienda sepa de inmediato quién escribe. */
    const saludo = encodeURIComponent(
      `Hola Bendito Equilibrio, soy ${v.nombre_catalogo || v.nombre} (${v.codigo}).`
    );
    $$('[data-be-soporte]').forEach((a) => {
      a.href = `https://wa.me/${WA_BENDITO}?text=${saludo}`;
    });

    /* Los productos se pintan una sola vez; el negocio, cada vez que se entra
       al portal, porque sus cifras cambian con los pedidos. */
    if (!$('[data-be-productos-lista]').childElementCount) {
      pintarProductos();
      pintarPedido();
    }
    pintarNegocio();
  }

  /* Estado del portal. Va arriba porque `enrutar` lo lee, y `enrutar` se
     define antes que el bloque del formulario. */
  let enviada = false;
  let etapaActual = 1;
  /* Lo poco que la confirmación necesita saber de la solicitud: cómo llamarla
     y su referencia. Nada sensible sobrevive al envío. */
  let solicitudEnviada = null;

  window.addEventListener('hashchange', enrutar);

  $$('[data-ir]').forEach((b) => {
    b.addEventListener('click', () => {
      window.location.hash = '#/' + b.dataset.ir;
    });
  });

  /* El enlace a las condiciones vive DENTRO del <label> de la casilla. Sin
     frenar la propagación, pulsarlo marcaría "acepto" de camino al documento:
     la persona aceptaría sin haberlo leído, justo al ir a leerlo.

     Solo se frena la propagación, NO el `preventDefault`: el enlace tiene que
     seguir abriendo la página en su pestaña nueva. */
  const verCondiciones = $('[data-ver-condiciones]');
  if (verCondiciones) {
    verCondiciones.addEventListener('click', (e) => e.stopPropagation());
  }

  /* --------------------------------------------------------- luz que persigue */

  /* Solo en dispositivos con puntero de verdad. En un móvil no hay cursor y
     escuchar pointermove sería gasto puro. */
  if (window.matchMedia('(pointer: fine)').matches) {
    const foco = $('[data-foco]');
    let pendiente = false;
    let x = 50;
    let y = 40;

    window.addEventListener('pointermove', (e) => {
      x = (e.clientX / window.innerWidth) * 100;
      y = (e.clientY / window.innerHeight) * 100;
      if (pendiente) return;
      pendiente = true;
      // Un solo escrito por fotograma: pointermove dispara muchas más veces.
      requestAnimationFrame(() => {
        foco.style.setProperty('--mx', x.toFixed(1) + '%');
        foco.style.setProperty('--my', y.toFixed(1) + '%');
        pendiente = false;
      });
    }, { passive: true });

    raiz.classList.add('is-puntero');
  }

  /* -------------------------------------------------------------- validación */

  const correoOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());

  /* El código que asigna Bendito Equilibrio al aprobar: BENDIT@ y tres
     dígitos. Se acepta en minúsculas y se normaliza, porque el teclado del
     móvil corrige solo y nadie debería fallar por eso. */
  const CODIGO = /^BENDIT@\d{3}$/;
  const normalizarCodigo = (v) => v.trim().toUpperCase().replace(/\s+/g, '');

  /** Cuenta solo dígitos: la gente escribe espacios, guiones y +57. */
  const digitos = (v) => v.replace(/\D/g, '');

  function pintarError(campo, mensaje) {
    const caja = campo.closest('.be-por__campo') || campo.closest('.be-por__etapa');
    const hueco = caja ? caja.querySelector(`[data-error-de="${campo.name}"]`) : null;
    if (caja) caja.classList.toggle('is-mal', Boolean(mensaje));
    if (hueco) hueco.textContent = mensaje || '';
  }

  /** Devuelve el mensaje de error, o '' si el campo está bien. */
  function revisar(campo) {
    const v = (campo.value || '').trim();

    if (campo.name === 'redes') {
      return redesMarcadas().length ? '' : 'Marca al menos una opción.';
    }
    if (campo.type === 'checkbox') {
      return campo.checked ? '' : 'Necesitamos que aceptes las condiciones.';
    }
    // El usuario de la red es el único opcional del formulario.
    if (campo.name === 'usuario') return '';
    if (!v) return 'Este dato nos falta.';
    if (campo.name === 'codigo' && !CODIGO.test(normalizarCodigo(v))) {
      /* 999 y no 001: el mensaje de error no debe repetir el código real de
         una vendedora. Ver el mismo motivo en be-portal-vendedor.liquid. */
      return 'El código tiene el formato BENDIT@999.';
    }
    if (campo.name === 'nombre_catalogo' && v.length < 2) {
      return 'Escribe el nombre que verán tus clientes.';
    }
    if (campo.name === 'documento' && digitos(v).length < 6) {
      return 'El documento parece incompleto.';
    }
    if (campo.name === 'nacimiento') {
      const edad = (Date.now() - new Date(v).getTime()) / 31557600000;
      if (Number.isNaN(edad)) return 'Revisa la fecha.';
      if (edad < 18) return 'Debes ser mayor de edad para ser Bendito Vendedor.';
      if (edad > 100) return 'Revisa la fecha de nacimiento.';
    }
    if (campo.type === 'email' && !correoOk(v)) return 'Revisa el correo: parece incompleto.';
    if (campo.name === 'whatsapp') {
      const n = digitos(v);
      // 10 dígitos en Colombia; 12 si escriben el 57 delante.
      if (n.length < 10 || n.length > 12) return 'El WhatsApp debe tener 10 dígitos.';
    }
    if ((campo.name === 'porque' || campo.name === 'donde') && v.length < 12) {
      return 'Cuéntanos un poco más.';
    }
    return '';
  }

  function validarGrupo(campos) {
    let primerMalo = null;
    campos.forEach((c) => {
      const msg = revisar(c);
      pintarError(c, msg);
      if (msg && !primerMalo) primerMalo = c;
    });
    if (primerMalo) {
      primerMalo.focus();
      primerMalo.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
    return !primerMalo;
  }

  /* ------------------------------------------------------ camino 1: ingresar */

  const formEntrar = $('[data-form-entrar]');
  const avisoEntrar = $('[data-aviso-entrar]');

  /* La flecha lleva al formulario. NO enfoca el campo a propósito: enfocarlo
     abre el teclado del móvil de golpe, que tapa media pantalla justo cuando
     la persona iba a mirar a dónde llegó. */
  const flecha = $('[data-flecha]');
  if (flecha && formEntrar) {
    flecha.addEventListener('click', () => {
      formEntrar.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  /**
   * PUNTO DE CONEXIÓN DE LA VERIFICACIÓN.
   *
   * Comprueba que el correo y el código pertenezcan al MISMO vendedor y que
   * su estado sea ACTIVO. Hoy devuelve `conectado: false` porque no hay nada
   * detrás; cuando lo haya, se cambia solo el cuerpo de esta función.
   *
   * NO comparar códigos aquí dentro ni traerse la lista de vendedores al
   * navegador: cualquier cosa que llegue al cliente es pública, y eso
   * filtraría los códigos de las seis vendedoras de golpe. La comprobación
   * tiene que ocurrir del otro lado, devolviendo solo sí o no.
   *
   * @returns {Promise<{conectado: boolean, ok?: boolean}>}
   */
  /**
   * Calcula la HUELLA: el identificador del documento en `vendedores`.
   *
   *   sha256( correo_en_minúsculas + '|' + CÓDIGO_EN_MAYÚSCULAS )
   *
   * ⚠️ Esta fórmula está escrita DOS VECES: aquí y en
   * `scripts/huella-vendedor.mjs`, que es con el que se calculan las huellas
   * para cargar las fichas en Firebase. Si dejan de coincidir, nadie entra y
   * el fallo no dice por qué. Cualquier cambio va en los dos sitios.
   */
  async function huellaDe(correo, codigo) {
    const limpio = `${correo.trim().toLowerCase()}|${codigo.trim().toUpperCase()}`;
    const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(limpio));
    return [...new Uint8Array(bytes)].map((b) => b.toString(16).padStart(2, '0')).join('');
  }

  /** Los campos públicos de la ficha. Lo que no esté aquí no sale de Firestore. */
  const CAMPOS_PUBLICOS = [
    'codigo', 'correo', 'nombre', 'nombre_catalogo',
    'whatsapp', 'catalogo', 'estado', 'aprobado',
  ];

  /** Firestore devuelve los valores tipados; se aplanan a texto llano. */
  function aplanar(fields) {
    const d = {};
    for (const k of CAMPOS_PUBLICOS) {
      const v = fields?.[k];
      if (!v) continue;
      d[k] = v.stringValue ?? v.integerValue ?? v.booleanValue ?? '';
    }
    return d;
  }

  /**
   * Comprueba que el correo y el código pertenezcan al MISMO vendedor y que
   * su estado sea ACTIVO.
   *
   * Pide UN documento concreto por su huella. No lista la colección: las
   * reglas lo prohíben, y es lo que impide que nadie pueda enumerar a los
   * vendedores ni averiguar qué códigos existen.
   *
   * @returns {Promise<{conectado:boolean, ok?:boolean, motivo?:string, vendedor?:object}>}
   */
  async function verificarAcceso(correo, codigo) {
    if (!hayFirebase) return { conectado: false };

    const huella = await huellaDe(correo, codigo);
    const url =
      `https://firestore.googleapis.com/v1/projects/${cfg.projectId}` +
      `/databases/(default)/documents/vendedores/${huella}?key=${cfg.apiKey}`;

    let res;
    try {
      res = await fetch(url, { headers: { Accept: 'application/json' } });
    } catch (_) {
      // Sin red o Firestore caído. No es culpa de quien escribe: se distingue.
      return { conectado: true, ok: false, motivo: 'red' };
    }

    /* 404 = no existe esa huella. Puede ser código errado, correo errado o
       vendedor inexistente: NO se distingue a propósito. Decirlo permitiría
       averiguar qué correos están registrados probando uno por uno.
       403 = las reglas rechazaron; se trata igual, sin dar pistas. */
    if (res.status === 404 || res.status === 403) {
      return { conectado: true, ok: false, motivo: 'no-coincide' };
    }
    if (!res.ok) return { conectado: true, ok: false, motivo: 'red' };

    const doc = await res.json();
    const vendedor = aplanar(doc.fields);

    if (vendedor.estado !== 'activo') {
      return { conectado: true, ok: false, motivo: 'inactivo' };
    }

    return { conectado: true, ok: true, vendedor };
  }

  /* ------------------------------------------------------------- la sesión */

  const CLAVE_SESION = 'be-portal-sesion';

  /* Guarda SOLO los campos públicos de la ficha, los mismos que ya viajaron
     por la red. Nunca el código ni la huella: si alguien mira el
     almacenamiento del navegador, no encuentra con qué volver a entrar.

     Esto identifica, NO autoriza. Cualquier dato de verdad sensible tendrá
     que pedirse a Firestore con las reglas puestas, no fiarse de aquí. */
  function abrirSesion(vendedor) {
    try {
      sessionStorage.setItem(CLAVE_SESION, JSON.stringify(vendedor));
    } catch (_) {
      /* Modo incógnito o almacenamiento lleno: la sesión es una comodidad,
         no puede tumbar el ingreso. */
    }
  }

  function leerSesion() {
    try {
      const crudo = sessionStorage.getItem(CLAVE_SESION);
      return crudo ? JSON.parse(crudo) : null;
    } catch (_) {
      return null;
    }
  }

  function cerrarSesion() {
    try {
      sessionStorage.removeItem(CLAVE_SESION);
    } catch (_) {
      /* Sin almacenamiento no hay sesión que cerrar. */
    }
  }

  /* `sessionStorage` y no `localStorage` a propósito: se borra al cerrar la
     pestaña. En un celular prestado o compartido, que la sesión no sobreviva
     es lo correcto. */
  let vendedorActivo = leerSesion();

  const AVISO_SIN_CONEXION =
    'El ingreso al portal todavía no está activo. Estamos terminándolo. ' +
    'Si ya eres Bendito Vendedor, tu asesor te avisará por WhatsApp en cuanto puedas entrar.';

  /* El mensaje exacto para cuando la verificación exista y no cuadre. No dice
     cuál de los dos datos falla: decirlo permitiría averiguar qué correos
     están registrados probando uno por uno. */
  const AVISO_NO_COINCIDE =
    'Los datos ingresados no corresponden a un Bendito Vendedor activo.';

  /* Un vendedor que existe pero está retirado o pendiente SÍ merece saberlo:
     no es un dato que se pueda averiguar sin tener ya sus credenciales. */
  const AVISO_INACTIVO =
    'Tu perfil de Bendito Vendedor no está activo en este momento. ' +
    'Escríbele a tu asesor por WhatsApp para reactivarlo.';

  /* Fallo de red o de Firestore. Se distingue del resto porque no es culpa de
     quien escribe, y repetir los datos no lo arregla. */
  const AVISO_RED =
    'No pudimos conectarnos en este momento. Revisa tu conexión e inténtalo de nuevo.';

  function mostrarAviso(texto) {
    avisoEntrar.hidden = false;
    avisoEntrar.textContent = texto;
    avisoEntrar.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  if (formEntrar) {
    /* El código se normaliza al salir del campo: el teclado del móvil pone
       mayúscula inicial y autocorrige, y nadie debería fallar por eso. */
    const campoCodigo = formEntrar.elements.codigo;
    campoCodigo.addEventListener('blur', () => {
      if (campoCodigo.value.trim()) campoCodigo.value = normalizarCodigo(campoCodigo.value);
    });

    formEntrar.addEventListener('submit', async (e) => {
      e.preventDefault();
      campoCodigo.value = normalizarCodigo(campoCodigo.value);

      const campos = [...formEntrar.elements].filter((el) => el.name);
      if (!validarGrupo(campos)) return;

      const boton = formEntrar.querySelector('[type="submit"]');
      const textoBoton = boton.textContent;
      boton.disabled = true;
      boton.textContent = 'Verificando…';

      let r;
      try {
        r = await verificarAcceso(
          formEntrar.elements.correo.value.trim().toLowerCase(),
          campoCodigo.value
        );
      } finally {
        // El botón se libera pase lo que pase: si no, un fallo lo deja muerto.
        boton.disabled = false;
        boton.textContent = textoBoton;
      }

      if (!r.conectado) {
        mostrarAviso(AVISO_SIN_CONEXION);
        return;
      }
      if (!r.ok) {
        if (r.motivo === 'inactivo') mostrarAviso(AVISO_INACTIVO);
        else if (r.motivo === 'red') mostrarAviso(AVISO_RED);
        else mostrarAviso(AVISO_NO_COINCIDE);
        return;
      }

      /* Entró. Se abre sesión y se limpia el formulario: dejar el código a la
         vista en un celular prestado no aporta nada. */
      vendedorActivo = r.vendedor;
      abrirSesion(r.vendedor);
      formEntrar.reset();
      avisoEntrar.hidden = true;

      window.location.hash = '#/portal';
    });
  }

  /* ------------------------------------------ acciones dentro del portal */

  $$('[data-salir]').forEach((b) => {
    b.addEventListener('click', () => {
      vendedorActivo = null;
      cerrarSesion();
      window.location.hash = '#/';
    });
  });

  /**
   * CONFIRMAR PEDIDO — abre WhatsApp con el pedido del vendedor ya escrito.
   *
   * ⚠️ ESTE BOTÓN ESTUVO ROTO Y HAY QUE ENTENDER POR QUÉ, PARA NO REPETIRLO.
   * Antes llamaba a `guardarPedido()`, que era un tapón: siempre devolvía
   * "no guardado" porque la colección de pedidos nunca se creó. Así que el
   * botón siempre caía en la rama de error, mostraba "el envío automático
   * todavía no está conectado" y exigía un SEGUNDO clic sobre un enlace.
   * Para el vendedor eso se ve como un botón que no funciona — una vendedora
   * llamó a reportarlo. Un camino de respaldo que exige un clic extra y se
   * anuncia como avería no es un respaldo: es una avería.
   *
   * ⚠️ `window.open` VA DENTRO DEL CLIC, SIN `await` ANTES.
   * Safari e iOS bloquean la ventana si se abre después de una promesa: deja
   * de contar como gesto del usuario. La versión anterior hacía `await` y por
   * eso además necesitaba el enlace manual. Aquí no hay nada asíncrono en el
   * camino, y por eso se abre siempre.
   */
  $$('[data-be-confirmar]').forEach((b) => {
    b.addEventListener('click', () => {
      if (!vendedorActivo || !pedido.size) return;
      const aviso = $('[data-be-aviso-pedido]');
      const datos = armarPedido();

      const detalle = datos.lineas
        .map((l) => `• ${l.cantidad} × ${l.nombre} — ${pesos(l.precio_vendedor * l.cantidad)}`)
        .join('\n');

      /* El CÓDIGO abre el mensaje. Quien atiende los pedidos ve decenas de
         conversaciones al día: con el código en la primera línea identifica al
         vendedor de un vistazo, sin abrir el chat ni leer el nombre. */
      const texto = [
        `${datos.vendedor_codigo} · PEDIDO DE VENDEDOR`,
        datos.vendedor_nombre,
        '',
        detalle,
        '',
        `Unidades: ${datos.unidades}`,
        `Total cliente: ${pesos(datos.total_cliente)}`,
        `*Total a pagar a Bendito Equilibrio: ${pesos(datos.total_bendito)}*`,
        `Comisión del vendedor: ${pesos(datos.comision)}`,
      ].join('\n');

      window.open(
        `https://wa.me/${WA_BENDITO}?text=${encodeURIComponent(texto)}`,
        '_blank', 'noopener'
      );

      /* Se registra para cuando exista la colección de pedidos. No se espera
         su resultado: el pedido ya va camino a WhatsApp y la confirmación del
         vendedor no puede depender de una escritura que hoy no ocurre. */
      guardarPedido(datos);

      pedido.clear();
      pintarProductos();
      pintarPedido();

      aviso.hidden = false;
      aviso.textContent =
        'Pedido enviado por WhatsApp. Si no se abrió, revisa que el navegador '
        + 'no haya bloqueado la ventana.';
      aviso.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  });

  $$('[data-be-categoria]').forEach((b) => {
    b.addEventListener('click', () => {
      window.location.hash = '#/galeria/' + b.dataset.beCategoria;
    });
  });

  $$('[data-be-volver-categorias]').forEach((b) => {
    b.addEventListener('click', () => {
      window.location.hash = '#/galeria';
    });
  });

  /* ------------------------------------------------- galería de descargas
   *
   * Un `<a download>` NO sirve aquí: la imagen vive en la CDN de Shopify, que
   * es otro origen, y ante un origen distinto el navegador ignora `download`
   * y abre la imagen en una pestaña. Es justo lo que el dueño no quería.
   *
   * La única forma de descargar de verdad es traerse el archivo como blob y
   * crear un enlace local. Funciona porque la CDN de Shopify responde con
   * CORS abierto para los assets del tema.
   */
  $$('[data-be-descargar]').forEach((boton) => {
    boton.addEventListener('click', async () => {
      const url = boton.dataset.beDescargar;
      const nombre = boton.dataset.beNombreArchivo || 'bendito-equilibrio.jpg';
      const original = boton.innerHTML;

      boton.disabled = true;
      boton.textContent = 'Descargando…';

      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const blob = await res.blob();
        const local = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = local;
        a.download = nombre;
        document.body.append(a);
        a.click();
        a.remove();
        // Liberar el blob: si no, la imagen se queda en memoria hasta recargar.
        setTimeout(() => URL.revokeObjectURL(local), 1000);

        boton.textContent = 'Descargada ✓';
      } catch (_) {
        /* Si el blob falla —sin red, o CORS cerrado— se abre la imagen para
           que al menos pueda guardarla con una pulsación larga. Peor, pero
           mejor que un botón que no hace nada. */
        window.open(url, '_blank', 'noopener');
        boton.textContent = 'Abierta en otra pestaña';
      }

      setTimeout(() => {
        boton.disabled = false;
        boton.innerHTML = original;
      }, 2200);
    });
  });

  /* Compartir: en el móvil abre el menú del sistema, que es donde la gente
     ya sabe elegir WhatsApp. En escritorio no existe ese menú, así que se
     copia al portapapeles y se avisa. */
  $$('[data-compartir]').forEach((b) => {
    b.addEventListener('click', async () => {
      const v = vendedorActivo;
      if (!v || !v.catalogo) return;

      const url = new URL(v.catalogo, window.location.origin).href;
      const texto = `Mira mi catálogo de Bendito Equilibrio: ${url}`;
      const original = b.textContent;

      const avisar = (t) => {
        b.textContent = t;
        setTimeout(() => { b.textContent = original; }, 2600);
      };

      if (navigator.share) {
        try {
          await navigator.share({ title: 'Mi catálogo Bendito Equilibrio', text: texto, url });
          return;
        } catch (_) {
          // Cancelar el menú de compartir no es un error: no se avisa de nada.
          return;
        }
      }

      try {
        await navigator.clipboard.writeText(url);
        avisar('Enlace copiado ✓');
      } catch (_) {
        avisar('Copia el enlace de arriba');
      }
    });
  });

  const ayuda = $('[data-ayuda]');
  if (ayuda) {
    ayuda.addEventListener('click', (e) => {
      e.preventDefault();
      mostrarAviso(
        'Tu código lo asigna Bendito Equilibrio cuando aprueba tu solicitud, y te llega por WhatsApp. ' +
        'Si no lo tienes o no te funciona, escríbele a tu asesor por WhatsApp.'
      );
    });
  }

  /* ----------------------------------------------------- camino 2: solicitud */

  const formSolicitud = $('[data-form-solicitud]');
  if (!formSolicitud) {
    // Sin formulario no hay pasos, pero la portada tiene que pintarse igual.
    enrutar();
    return;
  }

  const etapas = $$('[data-etapa]', formSolicitud);
  const pasos = $$('.be-por__paso');
  const barra = $('[data-barra]');
  const btnAtras = $('[data-atras]');
  const btnSiguiente = $('[data-siguiente]');
  const btnEnviar = $('[data-enviar]');
  const resumen = $('[data-resumen]');
  const rotuloPaso = $('[data-rotulo-paso]');
  const tituloPaso = $('[data-titulo-paso]');

  /* El orden de esta tabla es el del resumen. */
  const ROTULOS = {
    nombre: 'Nombre completo',
    nombre_catalogo: 'Nombre en el catálogo',
    documento: 'Documento',
    nacimiento: 'Fecha de nacimiento',
    correo: 'Correo electrónico',
    whatsapp: 'WhatsApp',
    ciudad: 'Ciudad',
    departamento: 'Departamento',
    direccion: 'Dirección',
    experiencia: 'Experiencia en ventas',
    suplementos: 'Ha vendido suplementos',
    donde: 'Dónde piensa promocionar',
    redes: 'Redes',
    usuario: 'Usuario',
    alcance: 'Personas a las que puede llegar',
    porque: 'Por qué quiere trabajar con nosotros',
    dedicacion: 'Tiempo que puede dedicar',
    meta: 'Meta mensual',
    conociste: 'Cómo nos conoció',
  };

  /* El título cambia con el paso: acompaña el viaje en vez de quedarse fijo.
     La palabra en <span> es la que va más encendida. */
  const TITULOS = [
    null,
    ['Cuéntanos ', 'quién', ' eres'],
    ['Lo que ya ', 'sabes', ' hacer'],
    ['Hasta dónde ', 'quieres', ' llegar'],
    ['Revisa y ', 'confirma', ''],
  ];

  /** Las casillas de "redes" comparten nombre. Se valida el grupo una vez, no
   *  seis veces, o exigiría marcarlas todas. */
  const camposDe = (n) => {
    const vistos = new Set();
    return [...formSolicitud.elements].filter((el) => {
      if (!el.name) return false;
      if (el.closest('[data-etapa]')?.dataset.etapa !== String(n)) return false;
      if (el.type === 'checkbox' && el.name === 'redes') {
        if (vistos.has(el.name)) return false;
        vistos.add(el.name);
      }
      return true;
    });
  };

  const redesMarcadas = () =>
    [...formSolicitud.querySelectorAll('input[name="redes"]:checked')].map((c) => c.value);

  function pintarEtapa() {
    etapas.forEach((f) => {
      const activa = f.dataset.etapa === String(etapaActual);
      f.hidden = !activa;
      f.classList.toggle('is-activa', activa);
    });

    pasos.forEach((p) => {
      const n = Number(p.dataset.pasoN);
      p.classList.toggle('is-activo', n === etapaActual);
      p.classList.toggle('is-hecho', n < etapaActual);
    });

    barra.style.width = (etapaActual / etapas.length) * 100 + '%';

    btnAtras.hidden = etapaActual === 1;
    btnSiguiente.hidden = etapaActual === etapas.length;
    btnEnviar.hidden = etapaActual !== etapas.length;

    rotuloPaso.textContent = `Paso ${etapaActual} de ${etapas.length}`;

    const t = TITULOS[etapaActual];
    if (t) {
      tituloPaso.textContent = '';
      const marcado = document.createElement('span');
      marcado.className = 'be-por__acento';
      marcado.textContent = t[1];
      tituloPaso.append(t[0], marcado, t[2]);
    }

    tono(String(etapaActual));

    if (etapaActual === etapas.length) pintarResumen();
  }

  function datos() {
    const d = {};
    [...formSolicitud.elements].forEach((el) => {
      if (el.name && el.type !== 'checkbox') d[el.name] = (el.value || '').trim();
    });
    d.redes = redesMarcadas().join(', ');
    return d;
  }

  function pintarResumen() {
    const d = datos();
    resumen.innerHTML = '';
    const lista = document.createElement('dl');
    lista.className = 'be-por__resumen-lista';

    Object.keys(ROTULOS).forEach((k) => {
      if (!d[k]) return;
      const fila = document.createElement('div');
      fila.className = 'be-por__resumen-fila';
      const dt = document.createElement('dt');
      dt.textContent = ROTULOS[k];
      const dd = document.createElement('dd');
      dd.textContent = d[k];
      fila.append(dt, dd);
      lista.append(fila);
    });

    resumen.append(lista);
  }

  /* Borrador: si recargan a mitad de la solicitud no pierden lo escrito.
     El documento NO se guarda. Es un dato de identidad y el borrador queda en
     el navegador, que puede ser prestado o compartido; que lo tengan que
     volver a escribir es barato comparado con dejarlo ahí. */
  const NO_GUARDAR = ['documento'];

  function guardarBorrador() {
    try {
      const d = datos();
      NO_GUARDAR.forEach((k) => delete d[k]);
      localStorage.setItem(CLAVE_BORRADOR, JSON.stringify(d));
    } catch (_) {
      /* Modo incógnito o almacenamiento lleno: el borrador es un extra, no
         puede tumbar el formulario. */
    }
  }

  function recuperarBorrador() {
    try {
      const crudo = localStorage.getItem(CLAVE_BORRADOR);
      if (!crudo) return;
      const d = JSON.parse(crudo);
      [...formSolicitud.elements].forEach((el) => {
        if (!el.name || d[el.name] === undefined) return;
        if (el.type === 'checkbox') return;
        el.value = d[el.name];
      });
      // Las redes se guardaron como texto separado por comas.
      if (d.redes) {
        const marcadas = d.redes.split(',').map((s) => s.trim());
        formSolicitud.querySelectorAll('input[name="redes"]').forEach((c) => {
          c.checked = marcadas.includes(c.value);
        });
      }
    } catch (_) {
      /* Borrador ilegible: se ignora y se empieza en blanco. */
    }
  }

  formSolicitud.addEventListener('input', guardarBorrador);

  /* Al salir de un campo ya tocado se limpia el error en cuanto se corrige. */
  formSolicitud.addEventListener(
    'blur',
    (e) => {
      const el = e.target;
      if (el.name && el.closest('.be-por__campo')) pintarError(el, revisar(el));
    },
    true
  );

  btnSiguiente.addEventListener('click', () => {
    if (!validarGrupo(camposDe(etapaActual))) return;
    etapaActual = Math.min(etapaActual + 1, etapas.length);
    pintarEtapa();
    formSolicitud.scrollIntoView({ block: 'start', behavior: 'smooth' });
  });

  btnAtras.addEventListener('click', () => {
    etapaActual = Math.max(etapaActual - 1, 1);
    pintarEtapa();
  });

  /**
   * PUNTO DE CONEXIÓN DE LA FASE 2.
   *
   * Hoy no envía nada a ningún sitio: no hay servidor al que enviar. Devuelve
   * una promesa para que el día que haya backend baste con cambiar el cuerpo
   * de esta función; quien la llama ya la espera.
   */
  /** Firestore quiere los valores tipados. Aquí todo es texto. */
  const aFirestore = (obj) => {
    const fields = {};
    for (const [k, v] of Object.entries(obj)) fields[k] = { stringValue: String(v ?? '') };
    return fields;
  };

  /**
   * Guarda la solicitud en la colección `solicitudes`.
   *
   * Va por `:commit` y no por el `createDocument` de siempre por una razón
   * concreta: la regla exige `creada == request.time`, y esa igualdad SOLO se
   * cumple si la fecha la pone el servidor. Un `new Date()` del navegador no
   * coincidiría nunca —siempre hay unos milisegundos de diferencia— y todas
   * las solicitudes serían rechazadas. `setToServerValue: REQUEST_TIME` es lo
   * que `createDocument` no sabe hacer.
   *
   * `currentDocument: { exists: false }` impide pisar un documento existente.
   *
   * @returns {Promise<{ok: boolean, referencia?: string, motivo?: string}>}
   */
  async function enviarSolicitud(d) {
    if (!hayFirebase) return { ok: false, motivo: 'sin-config' };

    const id = (crypto.randomUUID?.() || String(Date.now())).replace(/-/g, '');
    const ruta =
      `projects/${cfg.projectId}/databases/(default)/documents/solicitudes/${id}`;

    const cuerpo = {
      writes: [
        {
          update: { name: ruta, fields: aFirestore(d) },
          updateTransforms: [{ fieldPath: 'creada', setToServerValue: 'REQUEST_TIME' }],
          currentDocument: { exists: false },
        },
      ],
    };

    try {
      const res = await fetch(
        `https://firestore.googleapis.com/v1/projects/${cfg.projectId}/databases/(default)/documents:commit?key=${cfg.apiKey}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(cuerpo),
        }
      );

      if (!res.ok) {
        const detalle = await res.text();
        console.warn('[Portal] Firestore rechazó la solicitud:', res.status, detalle);
        return { ok: false, motivo: 'rechazada' };
      }

      // Referencia corta para que la persona pueda citarla por WhatsApp.
      return { ok: true, referencia: id.slice(0, 8).toUpperCase() };
    } catch (_) {
      return { ok: false, motivo: 'red' };
    }
  }

  formSolicitud.addEventListener('submit', async (e) => {
    e.preventDefault();

    const acepto = formSolicitud.elements.acepto;

    /* Se revisan los pasos anteriores uno por uno. Si algo falla en un paso
       que ahora está oculto, el error quedaría invisible y el botón parecería
       roto: hay que llevar a la persona hasta allí. */
    for (let n = 1; n < etapas.length; n += 1) {
      if (!validarGrupo(camposDe(n))) {
        etapaActual = n;
        pintarEtapa();
        return;
      }
    }

    const msgAcepto = revisar(acepto);
    pintarError(acepto, msgAcepto);
    if (msgAcepto) return;

    /* Deshabilitar ANTES de la petición: sin esto, dos toques seguidos crean
       dos solicitudes, y en un celular lento eso pasa constantemente. */
    btnEnviar.disabled = true;
    btnEnviar.textContent = 'Enviando…';
    const avisoPedido = $('[data-be-aviso-solicitud]');
    if (avisoPedido) avisoPedido.hidden = true;

    const d = datos();
    /* El estado lo pone el código, nunca el formulario: la regla exige que
       toda solicitud nazca pendiente. `creada` la pone el servidor. */
    d.estado = 'pendiente';
    /* El WhatsApp se normaliza a dígitos con indicativo, igual que en las
       fichas de vendedor: así el número sirve para escribirle sin retocarlo. */
    const soloDigitos = digitos(d.whatsapp);
    d.whatsapp = soloDigitos.startsWith('57') ? soloDigitos : '57' + soloDigitos;

    const r = await enviarSolicitud(d);

    btnEnviar.disabled = false;
    btnEnviar.textContent = 'Enviar mi solicitud';

    /* Si Firestore no la aceptó NO se dice "recibida" ni se abre WhatsApp: se
       queda en el paso 4 para poder reintentar. Decir que llegó cuando no
       llegó es peor que el propio fallo. */
    if (!r.ok) {
      if (avisoPedido) {
        avisoPedido.hidden = false;
        avisoPedido.textContent =
          'No pudimos enviar tu solicitud todavía. Revisa tu conexión e inténtalo nuevamente.';
        avisoPedido.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
      return;
    }

    try {
      localStorage.removeItem(CLAVE_BORRADOR);
    } catch (_) {
      /* Sin almacenamiento no hay borrador que borrar. */
    }

    solicitudEnviada = {
      nombre: d.nombre_catalogo || d.nombre.split(' ')[0],
      referencia: r.referencia,
    };
    enviada = true;
    window.location.hash = '#/enviada';
  });

  /* ------------------------------------------------------------------ arranque */

  recuperarBorrador();
  pintarEtapa();
  enrutar();

  /* ------------------------------------------------------ puerta a la fase 2
   *
   * El panel privado se construirá aparte y necesita saber quién entró. En vez
   * de que hurgue en `sessionStorage` por su cuenta, se le da esta puerta: si
   * un día cambia dónde se guarda la sesión, se cambia aquí y el panel ni se
   * entera.
   *
   * `vendedor()` devuelve una COPIA. Si devolviera el objeto, el panel podría
   * modificarlo sin querer y la sesión quedaría distinta de lo que hay en
   * Firestore.
   *
   * Ojo: esto identifica, NO autoriza. Cualquier dato sensible que el panel
   * necesite tendrá que pedirlo a Firestore, donde mandan las reglas.
   */
  window.BenditoVendedor = {
    vendedor: () => (vendedorActivo ? { ...vendedorActivo } : null),
    activo: () => Boolean(vendedorActivo),
    salir() {
      vendedorActivo = null;
      cerrarSesion();
    },
  };
})();
